// Raccourcissement des numéros de commande POUR LA LISTE, et pour elle seule.
//
// 🔴 CE QU'ON RETIRE EST CE QUI NE DISTINGUE RIEN. Un numéro vaut
// `<Nom du stock> - <Nom du fournisseur> - <n>` (cf. `SEPARATEUR_NUMERO` côté
// serveur). Dans une liste scopée à UN stock, le premier segment est identique
// sur toutes les lignes : il occupe la colonne la plus à gauche, celle qu'on lit
// en premier, et il n'aide jamais à distinguer deux lignes.
//
// ⚠️ ON NE COUPE PAS À UN NOMBRE DE CARACTÈRES, ON COUPE À DES SEGMENTS
// COMMUNS — et c'est la différence entre un raccourci et une troncature.
// Couper à N caractères produirait `Stock Princi…` sur un stock au nom long et
// mangerait le numéro sur un nom court : la coupe dépendrait du décor, pas de
// l'information. Ici, seuls les segments IDENTIQUES SUR TOUTES LES LIGNES
// tombent — donc rien d'unique ne peut disparaître, par construction.
//
// ⚠️ ET LE NUMÉRO RESTE ENTIER PARTOUT AILLEURS : dans le détail, dans le PDF,
// dans l'e-mail au fournisseur. C'est une référence externe — un client la lit
// sur un document. Ce fichier ne sert QUE l'affichage d'une liste.

import { isolerLtr } from './bidi';

/** Le séparateur du serveur (`backend/routes/purchase_orders.py`). */
export const SEPARATEUR_NUMERO = ' - ';

/** Le signe qui dit qu'on a coupé. Sans lui, un numéro raccourci se lit comme un vrai numéro. */
export const ELISION = '…';

/**
 * Combien de segments de tête sont IDENTIQUES sur tous les numéros donnés.
 * Rend 0 s'il y a moins de deux numéros — sur une liste d'une seule ligne,
 * « commun » ne veut rien dire et tout serait élidé.
 */
export function segmentsCommuns(numeros) {
  const listes = (numeros || []).filter(Boolean).map((n) => n.split(SEPARATEUR_NUMERO));
  if (listes.length < 2) return 0;
  // On garde TOUJOURS le dernier segment : c'est celui qui numérote.
  const maxi = Math.min(...listes.map((l) => l.length)) - 1;
  // ⚠️ L'INDICE EST FIGÉ DANS `rang` À CHAQUE TOUR, PAS LU DEPUIS `n`. La
  // fermeture `(l) => l[n]` était appelée sur-le-champ, donc juste — mais elle
  // capturait une variable que la boucle réassigne, et rien dans sa forme ne
  // disait qu'elle ne survivrait pas au tour. *Une fermeture correcte par
  // l'ordre d'exécution le reste jusqu'au jour où on rend l'appel différé.*
  let n = 0;
  while (n < maxi) {
    const rang = n;
    if (!listes.every((l) => l[rang] === listes[0][rang])) break;
    n += 1;
  }
  return n;
}

/**
 * Rend une fonction qui raccourcit un numéro de CETTE liste.
 * Le calcul se fait une fois pour la page, pas une fois par ligne.
 */
export function raccourcisseurDeNumeros(numeros) {
  const communs = segmentsCommuns(numeros);
  return (numero) => {
    if (!numero) return '';
    if (communs === 0) return numeroAffichable(numero);
    const parts = numero.split(SEPARATEUR_NUMERO);
    if (parts.length <= communs) return numeroAffichable(numero);
    return numeroAffichable(ELISION + parts.slice(communs).join(SEPARATEUR_NUMERO));
  };
}

/**
 * Le numéro TEL QU'ON L'AFFICHE — isolé en gauche-droite.
 *
 * 🔴 UN NUMÉRO N'EST PAS DU TEXTE, C'EST UNE STRUCTURE : `<site> - <fournisseur>
 * - <n>`. Trois segments de natures différentes, dont deux peuvent être écrits
 * en hébreu et le troisième est toujours un nombre. **Mesuré sous RTL** :
 *   · `מרפאה ראשית - Office Solutions - 9` → les segments sortent dans l'ordre
 *     `Office Solutions - 9 - מרפאה ראשית`, le premier devenant le dernier ;
 *   · `…Office Solutions - 9` → **l'élision passe à la FIN**, donc le signe qui
 *     dit « on a coupé au début » se retrouve à l'autre bout.
 * L'isolement rend les deux identiques au rendu français — mesuré des deux côtés.
 *
 * ⚠️ ET UN NUMÉRO TOUT LATIN N'AVAIT AUCUN DÉFAUT : `Stock Principal - Office
 * Solutions - 9` sortait déjà dans le bon ordre. On isole quand même — *une
 * garde qui ne s'applique qu'aux cas déjà tombés attend le prochain*.
 *
 * 🔴 CE QUE CETTE FONCTION NE DOIT JAMAIS SERVIR : le NOM DE FICHIER du PDF
 * (`OrderDetailView`), la RECHERCHE (`OrderReception` filtre sur
 * `order.order_number` brut), et tout ce qui repart au serveur. Le résultat
 * porte deux caractères invisibles : *une valeur d'affichage qui remonte dans
 * une donnée est une donnée qu'on ne pourra plus comparer à elle-même.*
 */
export function numeroAffichable(numero) {
  return isolerLtr(numero);
}
